import _defineProperty from "@babel/runtime/helpers/defineProperty";
var _PopupUtils;
/*
 *                     NOTICE
 *            COPYRIGHT 2018 Ab Initio
 *       UNPUBLISHED -- ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

import { Direction } from "abreact/incubator/ui/SharedTypes";
import { Layer } from "abreact/incubator/ui/layer/Layer";
import { LayerManager } from "abreact/incubator/ui/manager/LayerManager";
import { AbPopupContext, ExplicitlyShownPopupContext, LayerGroupContext } from "abreact/incubator/ui/popup/PopupContext";
import { PopupPosition } from "abreact/incubator/ui/popup/PopupPosition";
import { PopupUtilsModal } from "abreact/incubator/ui/popup/PopupUtilsModal";
import { AbIterable } from "abweb/collection/AbIterable";
import { AbListHelper } from "abweb/collection/AbListHelper";
import { AbSet } from "abweb/collection/AbSet";
import { ClassUtils } from "abweb/utils/ClassUtils";
import { NumberUtils } from "abweb/utils/NumberUtils";
import { TypeUtils } from "abweb/utils/TypeUtils";
import * as React from "react";
import { tabbable } from 'tabbable';
/**
 * A default implementation of AbPopupHandle.
 */
export class PopupHandle {
  constructor(props) {
    _defineProperty(this, "untilClosed", void 0);
    _defineProperty(this, "layer", void 0);
    // TODO get rid of show
    _defineProperty(this, "show", () => {
      LayerManager.getLayerGroup(this.layer.layerGroupId).push(this.layer);
    });
    if (props.isDeclarative) {
      this.layer = new Layer(props.layerGroupId, props.popup, props.id);
    } else {
      this.layer = new Layer(props.layerGroupId, /*#__PURE__*/React.createElement(AbPopupContext.Provider, {
        key: TypeUtils.generateUniqueId("popup-context-provider"),
        value: {
          popupHandle: this
        }
      }, /*#__PURE__*/React.createElement(LayerGroupContext.Provider, {
        value: props.layerGroupId
      }, props.popup)), props.id);
    }
    const self = this;
    this.untilClosed = new Promise((resolve, reject) => {
      self.resolveUntilClosed = resolve;
    });
    this.close = this.close.bind(this);
  }
  close() {
    // possibly the layer has been removed in some other way, so make sure the untilClosed is resolved regardless
    this.resolveUntilClosed();
    if (!LayerManager.hasLayerGroup(this.layer.layerGroupId)) {
      return; // Layer group doesn't exist, so don't do anything.
    }
    const layer = LayerManager.getLayerGroup(this.layer.layerGroupId).getLayer(this.layer.id);
    if (layer === undefined) {
      return; // layer has already been removed, so don't remove it again
    }
    LayerManager.getLayerGroup(this.layer.layerGroupId).remove(this.layer);
  }
  get id() {
    return this.layer.id;
  }
  get layerGroupId() {
    return this.layer.layerGroupId;
  }
}

/**
 * A variant of popup handle that shows a popup only after a delay.
 *
 * Note: the handle can be closed even before the delay elapses, in
 * which case the popup will not be shown at all.
 */
export class DelayedPopupHandle extends PopupHandle {
  constructor(props) {
    super(props);
    _defineProperty(this, "closed", false);
    this.close = this.close.bind(this);
  }
  showAfterDelay(delay) {
    if (delay === 0) {
      this.showIfNotClosed();
    } else {
      setTimeout(() => this.showIfNotClosed(), delay);
    }
  }
  showIfNotClosed() {
    if (!this.closed) {
      this.show();
    }
  }
  close() {
    super.close();
    this.closed = true;
  }
}
// ABSDK-180 asks for popup 'owners' to default to the current page (and thus teardown the popups if user navigates away
// from the current page).  However, mhub doesn't use RTD infrastructure (NavigationService.*), so dynamically
// import the 'default popup owner function' into this class (and mhub won't do this)
export class PopupUtils {
  // cursor size in pixels for mouse positioner

  // Call from test infrastructure only
  static resetStatics() {
    PopupUtils._ownerPopupRegistryDefaultFn = undefined;
  }
  static set ownerPopupRegistryDefaultFn(defaultPopupOwnerFn) {
    PopupUtils._ownerPopupRegistryDefaultFn = defaultPopupOwnerFn;
  }

  /**
   * Show a popup above all other layers in the given layer group (default if not provided).
   *
   * Note: this method is the implementation for PopupManager.show but provides
   * more control. Eventually this method may become PopupManager.show.
   *
   * @param {React.ReactElement<any>} popup The popup to be shown to the user.
   * @param {ShowPopupProps} props Optional properties to control the showing of the popup.
   * @returns {AbPopupHandle} The popup handle of the popup that will be shown.
   */
  static showPopupExplicitly(popup, props = {}) {
    const id = props.id || popup.props.id;
    return PopupUtils.showPopup(/*#__PURE__*/React.createElement(ExplicitlyShownPopupContext.Provider, {
      value: true
    }, popup), {
      id,
      ...props
    });
  }

  /**
   * Show a popup above all other layers in the given layer group (default if not provided).
   *
   * @param {React.ReactElement<any>} popup The popup to be shown to the user.
   * @param {ShowPopupProps} props Optional properties to control the showing of the popup.
   * @returns {AbPopupHandle} The popup handle of the popup that will be shown.
   */
  static showPopup(popup, props = {}) {
    // Which layer for the popup? First, try the props, then the main layer.
    const layerGroupId = props.layerGroupId || LayerManager.DEFAULT_GROUP_ID;
    const id = props.id || popup.props.id;
    let popupHandle;
    if (props.initialDelay) {
      const delayedPopupHandle = popupHandle = new DelayedPopupHandle({
        layerGroupId: layerGroupId,
        id: id,
        popup: popup,
        isDeclarative: props.isDeclarative
      });
      delayedPopupHandle.showAfterDelay(props.initialDelay);
    } else {
      popupHandle = new PopupHandle({
        layerGroupId: layerGroupId,
        id: id,
        popup: popup,
        isDeclarative: props.isDeclarative
      });
      popupHandle.show();
    }
    this.addPopupRegistrySupport(props.ownerPopupRegistry, popupHandle);
    return popupHandle;
  }

  /**
   *  If there is an ownerPopupRegistry (will default to current page if undefined),
   *  register with it so that popup will be closed and unregistered
   *  when the ownerPopupRegistry is externally destroyed (such as onMajorRouteChange or component unmount).
   *  See ABSDK-180
   *
   * @param ownerPopupRegistry
   * @param popupHandle
   */
  static addPopupRegistrySupport(ownerPopupRegistry, popupHandle) {
    // Default ownerPopupRegistry to the current route so that it will teardown the popup on a
    // major route change (such as user hitting browser back button)
    // Do NOT default ownerPopupRegistry on 'null' as that is explicit value the PREVENTS defaulting
    if (ownerPopupRegistry === undefined && PopupUtils._ownerPopupRegistryDefaultFn !== undefined) {
      ownerPopupRegistry = PopupUtils._ownerPopupRegistryDefaultFn();
    }
    if (ownerPopupRegistry != null) {
      ownerPopupRegistry.registerPopup(popupHandle); // auto-unregisters when popup is closed
    }
  }

  /**
   * Returns the ID for the specified popup handle, or undefined if it doesn't have one.
   */
  static getPopupId(popupHandle) {
    return ClassUtils.isInstanceOf(popupHandle, PopupHandle) ? popupHandle.id : undefined;
  }

  /**
   * Returns the default positioner to use for the specified position, or undefined if there is none.
   */
  static defaultPositioner(position) {
    switch (position) {
      case PopupPosition.ABOVE:
      case PopupPosition.BELOW:
      case undefined:
        return PopupUtils.VERTICAL_POSITIONER;
      case PopupPosition.LEFT:
      case PopupPosition.RIGHT:
        return PopupUtils.HORIZONTAL_POSITIONER;
      case PopupPosition.CENTER:
      case PopupPosition.FIXED:
        return undefined;
    }
  }
  /**
   * A positioner that shows a popup above or below an element.
   *
   * In addition, this positioner will make the popup at least as wide as the element.
   */
  static createVerticalPositioner(verticalProps = {}) {
    return props => {
      const popupElement = props.popupElement;
      let relativeElementRect = props.relativeToClientRect;
      if (verticalProps.spacing !== undefined) {
        relativeElementRect = this.applyClientRectVerticalSpacing(relativeElementRect, verticalProps.spacing);
      }
      const {
        width: popupWidth,
        height: popupHeight
      } = this.getSafeElementDimensions(popupElement);

      // Position the left and width of the modal
      const preferredLeft = relativeElementRect.left;
      const alternateLeft = relativeElementRect.right - popupWidth;
      const leftPosition = PopupUtils.positionLeft({
        ...props,
        relativeToClientRect: relativeElementRect
      }, preferredLeft, alternateLeft, false);

      // Position the top and height of the modal to above or below the element
      const above = props.position === PopupPosition.ABOVE;
      const belowTop = relativeElementRect.bottom;
      const aboveTop = relativeElementRect.top - (verticalProps.defaultAboveHeight ?? popupHeight);
      const topPosition = PopupUtils.positionTop({
        ...props,
        preferredTop: above ? aboveTop : belowTop,
        alternateTop: above ? belowTop : aboveTop,
        alternateMaxValue: verticalProps.defaultAboveHeight,
        isPrimaryOffset: true,
        relativeToClientRect: relativeElementRect
      });
      return {
        top: topPosition.top,
        left: leftPosition.left,
        maxHeight: topPosition.maxHeight,
        height: topPosition.popupPosition === PopupPosition.ABOVE ? verticalProps.defaultAboveHeight : undefined,
        maxWidth: leftPosition.maxWidth,
        minWidth: verticalProps.matchWidth ? relativeElementRect.width : undefined,
        // Make the popup at least as wide as the relative element
        popupPosition: topPosition.popupPosition,
        resizeDirectionsToDisable: [topPosition.popupPosition === PopupPosition.ABOVE ? Direction.DOWN : Direction.UP]
      };
    };
  }
  /**
   * Custom popup positioner for toolbars. The requirements are very similar to a vertical positioner, but have
   * some toolbar-specific things sprinkled in for horizontal positioning/sizing. The minimum width is the largest
   * toolbar item, the maximum width is that of the toolbar element, and the left position tries to be close to
   * the popup button (origin) but slides to the left as necessary.
   */

  /**
   * A positioner that shows a popup to the left/right of an element.
   **/
  static createHorizontalPositioner(horizontalProps = {}) {
    return props => {
      const popupElement = props.popupElement;
      let relativeElementRect = props.relativeToClientRect;
      if (horizontalProps.spacing !== undefined) {
        relativeElementRect = this.applyClientRectHorizontalSpacing(relativeElementRect, horizontalProps.spacing);
      }
      const {
        width: popupWidth,
        height: popupHeight
      } = this.getSafeElementDimensions(popupElement);

      // Position the top and height of the modal
      const preferredTop = relativeElementRect.top;
      const alternateTop = relativeElementRect.bottom - popupHeight;
      const topPosition = PopupUtils.positionTop({
        ...props,
        preferredTop,
        alternateTop,
        isPrimaryOffset: false
      });

      // Position the popup either to the left or right of the element
      const popupToLeft = props.position === PopupPosition.LEFT;
      const leftmostLeft = relativeElementRect.left - popupWidth;
      const rightmostLeft = relativeElementRect.right;
      const leftPosition = PopupUtils.positionLeft(props, popupToLeft ? leftmostLeft : rightmostLeft, popupToLeft ? rightmostLeft : leftmostLeft, true);
      return {
        top: topPosition.top,
        left: leftPosition.left,
        maxHeight: topPosition.maxHeight,
        maxWidth: leftPosition.maxWidth,
        popupPosition: leftPosition.popupPosition,
        resizeDirectionsToDisable: [topPosition.popupPosition === PopupPosition.LEFT ? Direction.RIGHT : Direction.LEFT]
      };
    };
  }

  // Defaults to bottom right of (x, y), leaving space for the cursor. Moves to other side if out of space.
  static createMousePositioner(x, y) {
    return props => {
      const {
        width: popupWidth,
        height: popupHeight
      } = this.getSafeElementDimensions(props.popupElement);
      const preferredLeft = x + PopupUtils.CURSOR_SIZE;
      const alternateLeft = x - popupWidth - PopupUtils.CURSOR_SIZE;
      const leftPosition = PopupUtils.positionLeft(props, preferredLeft, alternateLeft, false);
      const preferredTop = y + PopupUtils.CURSOR_SIZE;
      const alternateTop = y - popupHeight - PopupUtils.CURSOR_SIZE;
      const topPosition = PopupUtils.positionTop({
        ...props,
        preferredTop,
        alternateTop,
        isPrimaryOffset: false
      });
      return {
        top: topPosition.top,
        left: leftPosition.left,
        maxHeight: topPosition.maxHeight,
        maxWidth: leftPosition.maxWidth,
        popupPosition: leftPosition.popupPosition
      };
    };
  }
  /**
   * Display a modal with 1-3 buttons for user actions.
   * @param {ReactNode} message typically a string, but can send any react node which is useful when want
   * to bold/italicize text or have more complex content.
   * @param {string} dialogTitle
   * @param {string} confirmButtonText
   * @param {string | null} rejectButtonText if null, don't show reject button
   * @param {boolean} showCancelButton show 'x' in upper right corner.  Defaults to true.
   * @returns {Promise<boolean | null>} True if confirm, false if reject, null if cancel
   */
  static displayModal(message, dialogTitle, confirmButtonText, rejectButtonText, showCancelButton, dataTestId, closeButton = true) {
    return new Promise(function (resolve, reject) {
      let layer = LayerManager.defaultLayerGroup.push(/*#__PURE__*/React.createElement(PopupUtilsModal, {
        message: message,
        dialogTitle: dialogTitle,
        confirmButtonText: confirmButtonText,
        rejectButtonText: rejectButtonText,
        showCancelButton: showCancelButton,
        dataTestId: dataTestId,
        closeButton: closeButton,
        confirmOnClick: () => {
          LayerManager.defaultLayerGroup.remove(layer);
          resolve(true);
        },
        rejectOnClick: () => {
          LayerManager.defaultLayerGroup.remove(layer);
          resolve(false);
        },
        cancelOnClick: () => {
          LayerManager.defaultLayerGroup.remove(layer);
          resolve(null);
        },
        onClose: () => {
          resolve(null);
        }
      }));
    });
  }

  /**
   * Returns true if the HTML element represents a modal layer.
   */
  static isModalLayerElement(htmlElement) {
    const elementClass = htmlElement.className;
    // Popups are modal only if they have the class "ab-modal-popup" specified
    if (elementClass.indexOf("ab-popup") >= 0) {
      return elementClass.indexOf("ab-modal-popup") > 0;
    }
    return true;
  }
  static isElementObscuredByLayer(element) {
    const isModal = (id, doc) => {
      return doc.getElementById(id)?.classList.contains("ab-modal-popup") ?? false;
    };
    // see if this element is itself in a layer of some kind
    const owningLayer = element.closest(".ab-overlay")?.children[0] ?? element.closest(".ab-popup");
    const ownerDocument = element.ownerDocument;
    const layers = LayerManager.getLayers();
    if (owningLayer == null) {
      return layers.any(layer => isModal(layer.id, ownerDocument));
    }
    for (const layer of layers) {
      if (layer.id === owningLayer.id) {
        return false;
      }
      if (isModal(layer.id, ownerDocument)) {
        return true;
      }
    }
    return false; // Should not be reachable, as this implies that the element is in a layer, and we never saw that layer during iteration.
  }

  /**
   * Returns an ordered list of the tabble elements within the specified element.
   */

  /**
   * Returns the first tabbable element that matches one of the specified selectors.
   */
  static findFirstTabbableElement(root, selectors = []) {
    const tabbableElements = AbListHelper.from(tabbable(root));
    for (const selector of selectors) {
      const matches = AbSet.from(AbIterable.fromNodeList(root.querySelectorAll(selector)));
      for (const match of tabbableElements) {
        if (matches.has(match)) {
          return match;
        }
      }
    }
    return tabbableElements.first();
  }
}
_PopupUtils = PopupUtils;
_defineProperty(PopupUtils, "CURSOR_SIZE", 15);
_defineProperty(PopupUtils, "_ownerPopupRegistryDefaultFn", void 0);
_defineProperty(PopupUtils, "WINDOW_CENTER_POSITIONER", props => {
  const w = props.popupElement.ownerDocument.defaultView ?? window.window;
  const {
    width: popupWidth,
    height: popupHeight
  } = _PopupUtils.getSafeElementDimensions(props.popupElement);
  const left = (w.innerWidth - popupWidth) / 2;
  const top = (w.innerHeight - popupHeight) / 2;
  return {
    popupPosition: PopupPosition.CENTER,
    left: NumberUtils.clamp(left, _PopupUtils.POSITIONER_DEFAULT_WINDOW_MARGIN),
    top: NumberUtils.clamp(top, _PopupUtils.POSITIONER_DEFAULT_WINDOW_MARGIN),
    maxWidth: w.innerWidth - 2 * _PopupUtils.POSITIONER_DEFAULT_WINDOW_MARGIN,
    maxHeight: w.innerHeight - 2 * _PopupUtils.POSITIONER_DEFAULT_WINDOW_MARGIN
  };
});
_defineProperty(PopupUtils, "createToolbarPositioner", toolbarProps => {
  return props => {
    if (toolbarProps.toolbarRef.current === null) return _PopupUtils.VERTICAL_POSITIONER(props);
    const popupElement = props.popupElement;
    const relativeElementRect = _PopupUtils.applyClientRectVerticalSpacing(props.relativeToClientRect, _PopupUtils.POSITIONER_DEFAULT_SPACING * 2);
    const toolbarRelativeElementRect = toolbarProps.toolbarRef.current.getBoundingClientRect();
    const toolbarWidth = Math.ceil(toolbarRelativeElementRect.width);
    const {
      width: popupWidth,
      height: popupHeight
    } = _PopupUtils.getSafeElementDimensions(popupElement);
    let left = Math.max(toolbarRelativeElementRect.left + toolbarWidth - popupWidth, toolbarRelativeElementRect.left);
    if (relativeElementRect.left + popupWidth < toolbarRelativeElementRect.left + toolbarWidth) {
      left = relativeElementRect.left;
    }
    const minWidth = Math.max(...AbIterable.fromNodeList(props.popupElement.querySelectorAll(".ab-popup-toolbar-group-item")).map(item => item.clientWidth)) + 24;

    // Position the top and height of the modal to above or below the element
    const above = props.position === PopupPosition.ABOVE;
    const belowTop = relativeElementRect.bottom;
    const aboveTop = relativeElementRect.top - popupHeight;
    const topPosition = _PopupUtils.positionTop({
      ...props,
      preferredTop: above ? aboveTop : belowTop,
      alternateTop: above ? belowTop : aboveTop,
      isPrimaryOffset: true,
      relativeToClientRect: relativeElementRect
    });
    return {
      top: topPosition.top,
      left: left,
      minWidth: minWidth,
      maxHeight: topPosition.maxHeight,
      maxWidth: toolbarWidth * 2,
      popupPosition: topPosition.popupPosition
    };
  };
});
_defineProperty(PopupUtils, "POSITIONER_DEFAULT_SPACING", 2);
_defineProperty(PopupUtils, "POSITIONER_DEFAULT_WINDOW_MARGIN", 5);
/**
 * A positioner that shows a popup above or below an element.
 *
 * In addition, this positioner will make the popup at least as wide as the element.
 */
_defineProperty(PopupUtils, "VERTICAL_POSITIONER", _PopupUtils.createVerticalPositioner({
  matchWidth: true,
  spacing: _PopupUtils.POSITIONER_DEFAULT_SPACING
}));
/**
 * A positioner that shows a popup above or below an element, without changing the size of the popup.
 */
_defineProperty(PopupUtils, "VERTICAL_FIXED_SIZE_POSITIONER", _PopupUtils.createVerticalPositioner({
  spacing: _PopupUtils.POSITIONER_DEFAULT_SPACING
}));
/**
 * A UX-approved positioner for dropdown behavior.
 */
_defineProperty(PopupUtils, "FILTERED_DROPDOWN_VERTICAL_POSITIONER", _PopupUtils.createVerticalPositioner({
  matchWidth: true,
  spacing: _PopupUtils.POSITIONER_DEFAULT_SPACING,
  defaultAboveHeight: 300
}));
/**
 * A positioner that shows a popup to the left or right of an element.
 */
_defineProperty(PopupUtils, "HORIZONTAL_POSITIONER", _PopupUtils.createHorizontalPositioner({
  spacing: _PopupUtils.POSITIONER_DEFAULT_SPACING
}));
/**
 * Position the modal at one of the two specified left positions.
 *
 * @param {PopupPositionerProps} props Configuration properties for the positioner.
 * @param {number} preferredLeft The preferred left position.
 * @param {number} alternateLeft The alternate left position if the preferred isn't appropriate.
 * @param {boolean} isPrimaryOffset if true, position will avoid relativeElementRect
 */
_defineProperty(PopupUtils, "positionLeft", (props, preferredLeft, alternateLeft, isPrimaryOffset = false) => {
  const position = _PopupUtils.positionOffset({
    preferredOffset: preferredLeft,
    alternateOffset: alternateLeft,
    popupSize: _PopupUtils.getSafeElementWidth(props.popupElement),
    windowSize: props.popupElement.ownerDocument.documentElement.clientWidth,
    clientStartOffset: props.relativeToClientRect.left,
    clientEndOffset: props.relativeToClientRect.right,
    isPrimaryOffset
  });
  return {
    left: position.value,
    maxWidth: position.maxValue,
    popupPosition: position.value < props.relativeToClientRect.left ? PopupPosition.LEFT : PopupPosition.RIGHT
  };
});
/**
 * Position the modal at one of the two specified top positions.
 */
_defineProperty(PopupUtils, "positionTop", props => {
  const position = _PopupUtils.positionOffset({
    preferredOffset: props.preferredTop,
    alternateOffset: props.alternateTop,
    alternateSizeCap: props.alternateMaxValue,
    popupSize: _PopupUtils.getSafeElementHeight(props.popupElement),
    windowSize: props.popupElement.ownerDocument.documentElement.clientHeight,
    clientStartOffset: props.relativeToClientRect.top,
    clientEndOffset: props.relativeToClientRect.bottom,
    isPrimaryOffset: props.isPrimaryOffset
  });
  return {
    top: position.value,
    maxHeight: position.maxValue,
    popupPosition: position.value < props.relativeToClientRect.top ? PopupPosition.ABOVE : PopupPosition.BELOW
  };
});
_defineProperty(PopupUtils, "moveOnScreen", args => {
  return NumberUtils.clamp(args.offset, _PopupUtils.POSITIONER_DEFAULT_WINDOW_MARGIN, args.windowSize - _PopupUtils.POSITIONER_DEFAULT_WINDOW_MARGIN - args.popupSize);
});
_defineProperty(PopupUtils, "AVAILABLE_SPACE_BUFFER_PX", 6);
_defineProperty(PopupUtils, "positionOffset", args => {
  let {
    preferredOffset,
    alternateOffset,
    clientStartOffset,
    clientEndOffset
  } = args;
  clientStartOffset = _PopupUtils.moveOnScreen({
    offset: clientStartOffset,
    popupSize: 0,
    windowSize: args.windowSize
  });
  clientEndOffset = _PopupUtils.moveOnScreen({
    offset: clientEndOffset,
    popupSize: 0,
    windowSize: args.windowSize
  });
  const alternatePopupSize = NumberUtils.clamp(args.popupSize, undefined, args.alternateSizeCap);
  const willFitInWindowWithMargin = (offset, newPopupSize) => {
    const margin = _PopupUtils.POSITIONER_DEFAULT_WINDOW_MARGIN;
    return offset >= margin && offset + newPopupSize <= args.windowSize - margin;
  };
  const calculateAvailableSpace = offset => {
    if (offset < clientStartOffset) return clientStartOffset - _PopupUtils.POSITIONER_DEFAULT_WINDOW_MARGIN;
    return args.windowSize - _PopupUtils.POSITIONER_DEFAULT_WINDOW_MARGIN - clientEndOffset;
  };
  const getMaxValue = (newPopupSize, availableSpace) => {
    if (newPopupSize > availableSpace - _PopupUtils.AVAILABLE_SPACE_BUFFER_PX) return availableSpace;
    return undefined;
  };
  if (!args.isPrimaryOffset) {
    const availableSpace = args.windowSize - 2 * _PopupUtils.POSITIONER_DEFAULT_WINDOW_MARGIN;
    // Fits inside window with margin and buffer
    if (willFitInWindowWithMargin(preferredOffset, args.popupSize)) return {
      value: preferredOffset,
      maxValue: getMaxValue(args.popupSize, availableSpace)
    };
    if (willFitInWindowWithMargin(alternateOffset, alternatePopupSize)) return {
      value: alternateOffset,
      maxValue: getMaxValue(alternatePopupSize, availableSpace)
    };
    // Needs to be scooted and/or given max value
    return {
      value: _PopupUtils.moveOnScreen({
        offset: preferredOffset,
        popupSize: Math.min(args.popupSize, availableSpace),
        windowSize: args.windowSize
      }),
      maxValue: getMaxValue(args.popupSize, availableSpace)
    };
  }
  const moveOffsetToValidPlace = (offset, newPopupSize) => {
    if (offset < args.clientStartOffset) return Math.max(_PopupUtils.POSITIONER_DEFAULT_WINDOW_MARGIN, Math.min(offset, clientStartOffset - newPopupSize));
    return Math.max(Math.min(offset, args.windowSize - _PopupUtils.POSITIONER_DEFAULT_WINDOW_MARGIN - newPopupSize), clientEndOffset);
  };

  // Make sure values make sense
  preferredOffset = moveOffsetToValidPlace(preferredOffset, args.popupSize);
  alternateOffset = moveOffsetToValidPlace(alternateOffset, alternatePopupSize);
  const preferredAvailableSpace = calculateAvailableSpace(preferredOffset);
  const alternateAvailableSpace = NumberUtils.clamp(calculateAvailableSpace(alternateOffset), undefined, args.alternateSizeCap);

  // Try preferred, then alternate, then choose the one that has more available space.
  if (preferredAvailableSpace >= args.popupSize) return {
    value: preferredOffset,
    maxValue: getMaxValue(args.popupSize, preferredAvailableSpace)
  };
  if (args.alternateSizeCap === undefined && alternateAvailableSpace >= alternatePopupSize) return {
    value: alternateOffset,
    maxValue: getMaxValue(alternatePopupSize, alternateAvailableSpace)
  };
  if (preferredAvailableSpace > alternateAvailableSpace) return {
    value: preferredOffset,
    maxValue: preferredAvailableSpace
  };
  return {
    value: alternateOffset,
    maxValue: alternateAvailableSpace
  };
});
_defineProperty(PopupUtils, "applyClientRectVerticalSpacing", (elementRect, spacing) => ({
  left: elementRect.left,
  right: elementRect.right,
  width: elementRect.width,
  top: elementRect.top - spacing,
  bottom: elementRect.bottom + spacing,
  height: elementRect.height + 2 * spacing,
  x: elementRect.left,
  y: elementRect.top - spacing,
  toJSON: () => {
    throw new Error("applyClientRectVerticalSpacing: cannot call toJSON");
  }
}));
_defineProperty(PopupUtils, "applyClientRectHorizontalSpacing", (elementRect, spacing) => ({
  left: elementRect.left - spacing,
  right: elementRect.right + spacing,
  width: elementRect.width + 2 * spacing,
  top: elementRect.top,
  bottom: elementRect.bottom,
  height: elementRect.height,
  x: elementRect.left - spacing,
  y: elementRect.top,
  toJSON: () => {
    throw new Error("applyClientRectHorizontalSpacing: cannot call toJSON");
  }
}));
_defineProperty(PopupUtils, "getSafeElementWidth", element => Math.ceil(element.getBoundingClientRect().width));
_defineProperty(PopupUtils, "getSafeElementHeight", element => Math.ceil(element.getBoundingClientRect().height));
_defineProperty(PopupUtils, "getSafeElementDimensions", element => {
  const boundingRect = element.getBoundingClientRect();
  return {
    width: Math.ceil(boundingRect.width),
    height: Math.ceil(boundingRect.height)
  };
});
_defineProperty(PopupUtils, "getTabbableElements", root => {
  return AbListHelper.from(tabbable(root).filter(element => !element.classList.contains("ab-popup-focus-trap")));
});
//# sourceMappingURL=PopupUtils.js.map
